/**
 * Stable, anonymous, per-browser identifier ("device id").
 *
 * Minted once (crypto UUID) and persisted in localStorage — deliberately
 * NOT part of the auth slice's cookie set, so it survives login, logout,
 * the post-login router.refresh()/reload, and token rotation. It
 * identifies the browser profile, not the account: the BE uses it to
 * correlate booking quotes minted anonymously with the create/release
 * that follows after the user signs in (IWG temp-reservation
 * continuity), and to attribute quote lifecycles per device.
 *
 * Sent as the `X-Device-Id` header on booking quote / create / release.
 * NOTE (deploy order): the BE must add `x-device-id` to its CORS
 * allow-list (django-cors-headers `CORS_ALLOW_HEADERS`) BEFORE this
 * ships — as of 2026-07 the QA API only allows
 * `accept, authorization, content-type, user-agent, x-csrftoken,
 * x-requested-with`, and an un-allowlisted header makes the browser
 * reject the whole request at preflight.
 */

const DEVICE_ID_STORAGE_KEY = 'hd_device_id';

export const DEVICE_ID_HEADER = 'X-Device-Id';

// Module-level memo: avoids a storage read per request and keeps the id
// stable within a page life even when storage is unavailable (blocked
// third-party storage / hardened private modes) — there, a fresh id is
// minted per page life and cross-login continuity degrades gracefully
// instead of erroring.
let cachedDeviceId: string | null = null;

const mintUuid = (): string => {
    if (
        typeof crypto !== 'undefined' &&
        typeof crypto.randomUUID === 'function'
    ) {
        return crypto.randomUUID();
    }
    // RFC 4122 v4 fallback — randomUUID needs a secure context, which
    // plain-http test deployments don't have.
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex
        .slice(6, 8)
        .join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`;
};

export const getDeviceId = (): string | null => {
    // SSR has no device identity — callers must omit the header there.
    if (typeof window === 'undefined') return null;
    if (cachedDeviceId) return cachedDeviceId;
    try {
        const existing = window.localStorage.getItem(DEVICE_ID_STORAGE_KEY);
        if (existing) {
            cachedDeviceId = existing;
            return existing;
        }
    } catch {
        /* storage read blocked — mint in-memory below */
    }
    const fresh = mintUuid();
    cachedDeviceId = fresh;
    try {
        window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, fresh);
    } catch {
        /* storage write blocked — in-memory id for this page life */
    }
    return fresh;
};

/** Header fragment for fetch calls — empty object during SSR. */
export const deviceIdHeader = (): Record<string, string> => {
    const id = getDeviceId();
    return id ? { [DEVICE_ID_HEADER]: id } : {};
};
